import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ROLES } from "@salesboard/contracts";
import { DetailFact, DetailModal } from "@/components/ui/DetailModal";
import { Tabs } from "@/components/ui/Tabs";
import { DataTable, type Column } from "@/components/ui/data-table/DataTable";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { ApiClientError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { useSession } from "@/features/auth/useSession";
import { AuditTimeline } from "@/features/audit/AuditTimeline";
import { useOpenDetail } from "@/app/useOpenDetail";
import { useCanonicalSlug } from "@/app/useCanonicalSlug";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { useOpportunities, useStages } from "@/features/opportunities/hooks";
import { openStageIds } from "@/features/opportunities/pipelineFilters";
import type { Opportunity } from "@salesboard/contracts";
import { useTeamMember, useTeamMemberHistory } from "./hooks";

/**
 * A colleague, openable by anyone from any owner name.
 *
 * The product had no such surface: owner names appeared everywhere and led nowhere, so
 * "whose pipeline is this, and what else are they carrying" could only be answered by
 * filtering the board by hand.
 *
 * Two audiences, one route. Everybody sees who they are and what they are carrying —
 * profile and open pipeline. The **administrative** timeline (invited, role changed,
 * deactivated) is Head-of-Sales-only, enforced by the API; hiding it here is presentation.
 */
export function TeamMemberModal(): React.JSX.Element {
  const t = useT();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const openDetail = useOpenDetail();

  const member = useTeamMember(slug);
  // A link taken before a rename opens the right record; this makes the address bar
  // catch up, so the stale slug is not copied onward from here.
  useCanonicalSlug(slug, member.data?.slug, "/user");
  useDocumentTitle(member.data?.name);
  const isHeadOfSales = session.data?.role === ROLES.headOfSales;
  const history = useTeamMemberHistory(member.data?.id, isHeadOfSales);
  const [tab, setTab] = useState<"pipeline" | "history">("pipeline");

  // Their live pipeline, through the ordinary list endpoint — everyone may read every
  // opportunity, so this needs no special permission.
  //
  // "Open" is the set of active open phases, read from configuration: the `status` filter it
  // used to send no longer exists, because a filter with no equivalent in the filter panel is
  // the mismatch specs/filtering-multiselect.md removes. Held until the phases arrive rather
  // than read unscoped — an unfiltered read would list this person's closed deals as their
  // live pipeline, which is a different fact, not a slightly early one.
  const stages = useStages();
  const openPhases = openStageIds(stages.data);
  const owned = useOpportunities(
    { owner: member.data?.id, stage: openPhases, perPage: 50, sort: "expectedCloseDate:asc" },
    { enabled: Boolean(member.data?.id) && openPhases.length > 0 },
  );

  if (member.isPending) {
    return (
      <DetailModal title={t("team.loading")}>
        <LoadingState rows={4} label={t("team.loading")} />
      </DetailModal>
    );
  }

  if (member.error || !member.data) {
    return (
      <DetailModal title={t("team.notFound")}>
        <ErrorState
          message={member.error instanceof ApiClientError ? member.error.message : t("team.loadFailed")}
          requestId={member.error instanceof ApiClientError ? member.error.requestId : "unknown"}
          onRetry={() => void member.refetch()}
          action={{ label: t("opportunity.backToBoard"), onClick: () => void navigate("/", { replace: true }) }}
        />
      </DetailModal>
    );
  }

  const person = member.data;

  const columns: Column<Opportunity>[] = [
    { key: "clientName", header: t("column.client"), render: (row) => <span className="font-medium">{row.clientName}</span> },
    { key: "name", header: t("column.opportunity"), render: (row) => row.name },
    { key: "stageName", header: t("column.stage"), render: (row) => row.stageName },
    { key: "nip", header: t("column.expectedRevenue"), align: "right", render: (row) => formatCurrency(row.nip) },
    { key: "expectedCloseDate", header: t("column.expectedClose"), render: (row) => formatDate(row.expectedCloseDate) },
  ];

  return (
    <DetailModal
      title={person.name}
      wide
      header={
        <div className="space-y-2">
          <div>
            <p className="text-muted-foreground text-sm">{t(`role.${person.role}`)}</p>
            <h2 className="text-xl font-semibold">{person.name}</h2>
          </div>

          {/* Load-bearing order (2026-08-25): what they are carrying, then what it is worth,
              then whether they are still here. State last because it is the exceptional fact —
              almost every profile reads "active", and it was occupying the first column. */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
            <DetailFact label={t("team.openOpportunities")}>{person.openOpportunities}</DetailFact>
            <DetailFact label={t("team.openPipeline")}>{formatCurrency(person.openPipelineValue)}</DetailFact>
            {/* State carries a word, never colour alone — and a former colleague still owns
                their history, so the profile says so plainly rather than 404ing. */}
            <DetailFact label={t("team.state")}>{t(`userState.${person.state}`)}</DetailFact>
          </dl>
        </div>
      }
    >
      {/* Tabs rather than two stacked sections: the pipeline is what a reader opens a profile
          for, and the account history — administrative, Head-of-Sales-only — used to sit below
          a table of unbounded length, which is to say out of sight. A profile with no history
          tab renders the pipeline alone, without a one-tab strip. */}
      <Tabs
        label={t("team.sections")}
        idPrefix="team"
        tabs={
          isHeadOfSales
            ? [
                { value: "pipeline" as const, label: t("team.openOpportunities") },
                { value: "history" as const, label: t("team.accountHistory") },
              ]
            : [{ value: "pipeline" as const, label: t("team.openOpportunities") }]
        }
        active={tab}
        onChange={setTab}
      >
        {tab === "pipeline" && (
        <section>
          {owned.isPending && <LoadingState rows={4} label={t("team.pipelineLoading")} />}

          {owned.data && owned.data.items.length === 0 && (
            <EmptyState
              title={t("team.nothingOpen")}
              description={t("team.nothingOpenFor", person.name)}
            />
          )}

          {owned.data && owned.data.items.length > 0 && (
            <DataTable
              columns={columns}
              rows={owned.data.items}
              rowKey={(row) => row.id}
              // Replaces this modal rather than stacking on it: one detail at a time, and
              // closing steps back to this profile.
              onRowClick={(row) => openDetail(`/opportunity/${row.slug}`)}
              caption={t("team.ownedCaption", person.name)}
            />
          )}

          {owned.data && owned.data.total > owned.data.items.length && (
            // Capped rather than paginated here — but a silent clip would misstate how much
            // someone is carrying, which is the whole point of this section.
            <p className="text-muted-foreground mt-2 text-xs">
              {t("team.showingOf", owned.data.items.length, owned.data.total)}
            </p>
          )}
        </section>
        )}

        {tab === "history" && isHeadOfSales && (
          <section>
            <p className="text-muted-foreground mb-3 text-xs">{t("team.accountHistoryNote")}</p>
            {history.isPending && <LoadingState rows={3} label={t("team.accountHistoryLoading")} />}
            {history.error && (
              <ErrorState
                message={
                  history.error instanceof ApiClientError ? history.error.message : t("team.accountHistoryFailed")
                }
                requestId={history.error instanceof ApiClientError ? history.error.requestId : "unknown"}
                onRetry={() => void history.refetch()}
              />
            )}
            {history.data && <AuditTimeline events={history.data.items} total={history.data.items.length} showingAll />}
          </section>
        )}
      </Tabs>
    </DetailModal>
  );
}
